import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { adminManagementService } from '../../services/adminService';
import { Package, Search, ExternalLink } from 'lucide-react';
import Pagination from '../../components/admin/Pagination';
import './AdminPages.css';

function AdminApps() {
    const [searchParams, setSearchParams] = useSearchParams();
    const currentPage = parseInt(searchParams.get('page')) || 1;

    const [data, setData] = useState({ data: [], total: 0, totalPages: 0 });
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            if (searchTerm !== debouncedSearch) {
                setSearchParams(prev => {
                    const params = new URLSearchParams(prev);
                    params.set('page', '1');
                    return params;
                });
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        loadApps();
    }, [currentPage, debouncedSearch]);

    const loadApps = async () => {
        try {
            setLoading(true);
            const response = await adminManagementService.getApps({
                page: currentPage,
                limit: 20,
                search: debouncedSearch
            });
            setData(response);
        } catch (error) {
            console.error('Failed to load apps', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        setSearchParams({ page: newPage });
    };

    const apps = data.data || [];

    if (loading) return <div className="admin-loading">Loading apps...</div>;

    return (
        <div className="admin-page">
            <div className="admin-page-header">
                <div className="header-left">
                    <Package size={24} />
                    <h1>Manage Apps ({data.total})</h1>
                </div>
                <div className="header-search admin-search-bar">
                    <Search size={18} />
                    <input
                        type="text"
                        placeholder="Search apps, workspaces, uploaders..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="admin-table-container">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>App Name</th>
                            <th>Platform</th>
                            <th>Workspace</th>
                            <th>Uploader</th>
                            <th>Created</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {apps.map(app => (
                            <tr key={app.id}>
                                <td>
                                    <Link to={`/admin/apps/${app.id}`} className="admin-link">
                                        <div className="app-cell">
                                            <span className="app-name-bold">{app.name || 'Untitled'}</span>
                                            <span className="app-version-muted">v{app.version || '1.0.0'}</span>
                                        </div>
                                    </Link>
                                </td>
                                <td>
                                    <span className={`platform-badge ${app.platform}`}>
                                        {app.platform}
                                    </span>
                                </td>
                                <td>
                                    <Link to={`/admin/workspaces/${app.workspaceId}`} className="admin-link">
                                        {app.workspace?.name}
                                    </Link>
                                </td>
                                <td>
                                    <div className="uploader-info">
                                        <span className="uploader-email">{app.uploader?.email}</span>
                                    </div>
                                </td>
                                <td>{new Date(app.createdAt).toLocaleDateString()}</td>
                                <td>
                                    <Link to={`/admin/apps/${app.id}`} className="admin-action-link">
                                        <ExternalLink size={16} /> Details
                                    </Link>
                                </td>
                            </tr>
                        ))}
                        {apps.length === 0 && (
                            <tr>
                                <td colSpan="6" className="empty-state">No apps found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <Pagination
                currentPage={currentPage}
                totalPages={data.totalPages}
                onPageChange={handlePageChange}
            />
        </div>
    );
}

export default AdminApps;
